import { Stack } from '@mantine/core';
import { useApiListAuditLogs, type ActivityLogOut } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import type { ApiCatalogSort } from '../../api';
import { useSuspenseFeatureAccess } from '../../api/hooks/workspace/useSuspenseFeatureAccess';
import { EmptyState } from '../EmptyState';
import type { FILTER_OPTIONS_DIVIDER, FilterOption } from '../Filter';
import {
	FilterDropdownType,
	FilterOperator,
	FilterOptionType,
	FilterType,
} from '../Filter';
import {
	activeUsersAndGroupsGetItemsFn,
	buildEntityGetItemsFn,
	buildGetItemsByIdFromSearch,
	convertInitiatedByFilterToCatalogFilter,
} from '../Filter/filterConfigUtils';
import { UpgradeButton } from '../Settings/UpgradeButton';
import { TableV2 } from '../TableV2';
import { useColumns } from './hooks';

const DEFAULT_SORT: ApiCatalogSort = {
	field: 'updated_at',
	order: 'desc',
};

const FILTER_OPTIONS: Array<FilterOption | typeof FILTER_OPTIONS_DIVIDER> = [
	{
		label: 'Date',
		type: FilterOptionType.UPDATED_TIME,
		field: 'updated_at',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.Date,
			defaultOperator: FilterOperator.IsOnOrBefore,
		},
	},
	{
		label: 'Initiated by',
		type: FilterOptionType.PERFORMER_ID,
		field: 'performer_id',
		filterDropdownConfig: {
			searchPlaceholder: 'Search users',
			multipleCountsSuffix: 'users',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Multiple,
			getItems: activeUsersAndGroupsGetItemsFn,
			getItemsById: buildGetItemsByIdFromSearch,
			convertToCatalogFilter: convertInitiatedByFilterToCatalogFilter,
			isSetOverride: {
				operator: 'or',
				operands: [
					{
						operands: [],
						field: 'owners',
						operator: 'is_set',
						value: null,
					},
					{
						operands: [],
						field: 'owner_groups',
						operator: 'is_set',
						value: null,
					},
				],
			},
			hasIsNotSetOption: false,
		},
	},
	{
		label: 'Resource name',
		type: FilterOptionType.RESOURCE_ID,
		field: 'resource_id',
		filterDropdownConfig: {
			searchPlaceholder: 'Search resources',
			multipleCountsSuffix: 'resource',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Multiple,
			getItems: buildEntityGetItemsFn(EntityType.all),
			getItemsById: buildGetItemsByIdFromSearch,
			hasIsNotSetOption: false,
		},
	},
];

export function AuditLogs() {
	const columns = useColumns();
	const { auditLogAccess } = useSuspenseFeatureAccess();

	if (!auditLogAccess) {
		return (
			<EmptyState
				title="Upgrade to access Audit Logs"
				description="Review detailed workspace activity and security audit trails with audit logs."
				illustrationName="upgrade"
				includeGoBack={false}
				stateHeight="50vh"
				size="lg"
				withActions={<UpgradeButton feature="Audit Logs" size="md" />}
			/>
		);
	}

	return (
		<Stack spacing="md" py="md" data-testid="audit-log-table">
			<TableV2<ActivityLogOut>
				columns={columns}
				pluralTypeString="logs"
				// @ts-ignore
				usePaginationList={useApiListAuditLogs}
				useCodegenListInterface
				withInteractiveHeader={false}
				withStickyColumnBorder={false}
				defaultSort={DEFAULT_SORT}
				withFilter
				withFilterOptions={FILTER_OPTIONS}
			/>
		</Stack>
	);
}
